import {
  DecisionAction,
  DecisionActionCompact,
  DecisionActivity,
  DecisionActivityActor,
  DecisionActivityImpact,
  DecisionDetailItem,
  DecisionDocumentRef,
  DecisionEvidence,
  DecisionListItem,
  DecisionMissingInformation,
  DecisionOwner,
  DecisionQuery,
  DecisionSourceTrace,
  DecisionSummary,
  HomeIaActionInputProperty,
  HomeIaActionInputSchema,
} from "../../../models/home";
import {
  HomeActivityActorResponse,
  HomeActivityImpactResponse,
  HomeActivityNarrativeResponse,
  HomeDecisionDocumentRefResponse,
  HomeDecisionFiltersResponse,
  HomeDecisionItemResponse,
  HomeDecisionListItemResponse,
  HomeDecisionOwnerResponse,
  HomeDecisionSourceTraceResponse,
  HomeDecisionSummaryResponse,
  HomeEvidenceResponse,
  HomeIaActionInputPropertyResponse,
  HomeIaActionInputSchemaResponse,
  HomeMissingInformationResponse,
  HomeNextBestActionCompactResponse,
  HomeNextBestActionResponse,
} from "../../../models/home/dtos";
import {
  toOptionalState,
  toConfidenceCompactViewModel,
  toConfidenceViewModel,
  toOptionalExpectedState,
  toOptionalActionHttpMethod,
  toPriorityCompactViewModel,
  toPriorityViewModel,
  toRiskCompactViewModel,
  toRiskViewModel,
  toState
} from "./decision.adapter.status-mappers";
import {
  toActionViewModel,
  toAvailableActionsViewModel,
  toCompactActionViewModel,
  toCompactAvailableActionsViewModel
} from "./decision.adapter.entity-mappers.action-catalog";
import {
  toEvidenceViewModel,
  toMissingInformationViewModel,
  toSourceTraceViewModel
} from "./decision.adapter.entity-mappers.evidence-and-activity";

export function toSummaryViewModel(dto?: HomeDecisionSummaryResponse | null): DecisionSummary {
    const actWithoutRush = dto?.actWithoutRush ?? dto?.safeToIgnore ?? 0;

    return {
      total: dto?.total ?? 0,
      actNow: dto?.actNow ?? 0,
      actSoon: dto?.actSoon ?? 0,
      actWithoutRush,
      waiting: (dto?.waiting ?? 0) + (dto?.blocked ?? 0),
      blocked: 0,
      safeToIgnore: actWithoutRush,
      completed: dto?.completed ?? 0,
      averagePriority: dto?.averagePriority ?? 0,
      criticalRisk: dto?.criticalRisk ?? 0,
      missingInformationCount: dto?.missingInformationCount ?? 0,
      lowConfidenceCount: dto?.lowConfidenceCount ?? 0,
      nextRefreshAt: dto?.nextRefreshAt ?? undefined
    };
  }

export function toQueryViewModel(dto?: HomeDecisionFiltersResponse | null): DecisionQuery {
    return {
      state: toOptionalState(dto?.state),
      assignedToUserId: dto?.assignedToUserId ?? undefined,
      q: dto?.q ?? undefined,
      from: dto?.from ?? undefined,
      to: dto?.to ?? undefined
    };
  }

export function toListItemViewModel(dto: HomeDecisionListItemResponse): DecisionListItem {
    return {
      decisionId: dto.decisionId ?? "",
      state: toState(dto.state),
      title: dto.title ?? "",
      shortReason: dto.shortReason ?? "",
      documentCount: dto.documentCount ?? 0,
      primaryDocument: toDocumentRefViewModel(dto.primaryDocument),
      nextBestAction: toCompactActionViewModel(dto.nextBestAction),
      availableActions: toCompactAvailableActionsViewModel(dto.availableActions),
      priority: toPriorityCompactViewModel(dto.priority),
      risk: toRiskCompactViewModel(dto.risk),
      confidence: toConfidenceCompactViewModel(dto.confidence),
      missingInformationCount: dto.missingInformationCount ?? 0,
      owner: toOwnerViewModel(dto.owner),
      dueAt: dto.dueAt ?? undefined,
      updatedAt: dto.updatedAt ?? "",
      traceId: dto.traceId ?? undefined
    };
  }

export function toDetailItemViewModel(dto: HomeDecisionItemResponse): DecisionDetailItem {
    return {
      decisionId: dto.decisionId ?? "",
      state: toState(dto.state),
      title: dto.title ?? "",
      shortReason: dto.shortReason ?? "",
      documents: (dto.documents ?? []).map((document) => toDocumentRefViewModel(document)).filter(
        (document): document is DecisionDocumentRef => !!document
      ),
      nextBestAction: toActionViewModel(dto.nextBestAction),
      availableActions: toAvailableActionsViewModel(dto.availableActions),
      priority: toPriorityViewModel(dto.priority),
      risk: toRiskViewModel(dto.risk),
      confidence: toConfidenceViewModel(dto.confidence),
      evidence: (dto.evidence ?? []).map((item) => toEvidenceViewModel(item)),
      missingInformation: (dto.missingInformation ?? []).map((item) => toMissingInformationViewModel(item)),
      owner: toOwnerViewModel(dto.owner),
      dueAt: dto.dueAt ?? undefined,
      updatedAt: dto.updatedAt ?? "",
      traceId: dto.traceId ?? undefined,
      sourceTrace: toSourceTraceViewModel(dto.sourceTrace)
    };
  }

export function toDocumentRefViewModel(
    dto?: HomeDecisionDocumentRefResponse | null
  ): DecisionDocumentRef | undefined {
    if (!dto?.documentId) {
      return undefined;
    }

    return {
      documentId: dto.documentId,
      classificationId: dto.classificationId ?? undefined,
      title: dto.title ?? dto.originalFilename ?? dto.documentId,
      originalFilename: dto.originalFilename ?? undefined,
      publicUrl: dto.publicUrl ?? undefined
    };
  }

export function toOwnerViewModel(dto?: HomeDecisionOwnerResponse | null): DecisionOwner | undefined {
    if (!dto?.userId && !dto?.displayName) {
      return undefined;
    }

    return {
      userId: dto.userId ?? undefined,
      displayName: dto.displayName ?? undefined
    };
  }
